import React, { useState, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import { Button, Badge } from 'react-bootstrap';
import BounceLoader from 'react-spinners/BounceLoader';
import { getLeadVillage, getLeadBlock, getLeadDistrict, getLeadState } from './HouseholdColumns';

const mapContainerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '14px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
};

const defaultCenter = {
    lat: 20.5937,
    lng: 78.9629
};

const LOCATION_CENTROIDS = {
    'dantewada': { lat: 18.8943, lng: 81.3508 },
    'balaghat': { lat: 21.8129, lng: 80.1843 },
    'betul': { lat: 21.9008, lng: 77.9015 },
    'kodagu': { lat: 12.4244, lng: 75.7382 },
    'coorg': { lat: 12.4244, lng: 75.7382 },
    'tamenglong': { lat: 24.9856, lng: 93.4907 },
    'varanasi': { lat: 25.3176, lng: 82.9739 },
    'anakapalle': { lat: 17.6896, lng: 83.0024 },
    'chittoor': { lat: 13.2172, lng: 79.1003 },
    'darjeeling': { lat: 27.0410, lng: 88.2663 },
    'alluri sitharama raju': { lat: 17.6896, lng: 83.0024 },
    'anantapur': { lat: 14.6819, lng: 77.6006 },
    'changlang': { lat: 27.1350, lng: 95.7380 },
    'hamirpur': { lat: 31.6862, lng: 76.5213 },
    'andhra pradesh': { lat: 15.9129, lng: 79.7400 },
    'arunachal pradesh': { lat: 28.2180, lng: 94.7278 },
    'assam': { lat: 26.2006, lng: 92.9376 },
    'bihar': { lat: 25.0961, lng: 85.3131 },
    'chhattisgarh': { lat: 21.2787, lng: 81.8661 },
    'goa': { lat: 15.2993, lng: 74.1240 },
    'gujarat': { lat: 22.2587, lng: 71.1924 },
    'haryana': { lat: 29.0588, lng: 76.0856 },
    'himachal pradesh': { lat: 31.1048, lng: 77.1734 },
    'jharkhand': { lat: 23.6102, lng: 85.2799 },
    'karnataka': { lat: 15.3173, lng: 75.7139 },
    'kerala': { lat: 10.8505, lng: 76.2711 },
    'madhya pradesh': { lat: 22.9734, lng: 78.6569 },
    'maharashtra': { lat: 19.7515, lng: 75.7139 },
    'manipur': { lat: 24.6637, lng: 93.9063 },
    'meghalaya': { lat: 25.4670, lng: 91.3662 },
    'mizoram': { lat: 23.1645, lng: 92.9376 },
    'nagaland': { lat: 26.1584, lng: 94.5624 },
    'odisha': { lat: 20.9517, lng: 85.0985 },
    'punjab': { lat: 31.1471, lng: 75.3412 },
    'rajasthan': { lat: 27.0238, lng: 74.2179 },
    'sikkim': { lat: 27.5330, lng: 88.5122 },
    'tamil nadu': { lat: 11.1271, lng: 78.6569 },
    'telangana': { lat: 18.1124, lng: 79.0193 },
    'tripura': { lat: 23.9408, lng: 91.9882 },
    'uttar pradesh': { lat: 26.8467, lng: 80.9462 },
    'uttarakhand': { lat: 30.0668, lng: 79.0193 },
    'west bengal': { lat: 22.9868, lng: 87.8550 },
    'delhi': { lat: 28.7041, lng: 77.1025 },
    'jammu & kashmir': { lat: 33.7782, lng: 76.5762 },
    'jammu and kashmir': { lat: 33.7782, lng: 76.5762 },
    'ladakh': { lat: 34.1526, lng: 77.5771 },
    'puducherry': { lat: 11.9416, lng: 79.8083 },
    'chandigarh': { lat: 30.7333, lng: 76.7794 }
};

// Strict Indian Territory Bounding Check (Latitude 6°N - 37.5°N, Longitude 68°E - 97.5°E)
const isWithinIndia = (lat, lng) => {
    return lat >= 6.0 && lat <= 37.5 && lng >= 68.0 && lng <= 97.5;
};

const extractCoordinates = (lead) => {
    let rawLat = lead.lat ??
        lead.latitude ??
        lead.userInfo?.address?.location?.latitude ??
        lead.userInfo?.address?.location?.coordinates?.[1] ??
        lead.surveyData?.stages?.[0]?.form_data?.lat ??
        lead.surveyData?.stages?.[0]?.form_data?.latitude ??
        lead.fieldViewData?.[0]?.lat ??
        lead.fieldViewData?.[0]?.latitude ??
        lead.fieldViewData?.[0]?.form_data?.lat ??
        lead.location?.latitude;

    let rawLng = lead.lng ??
        lead.longitude ??
        lead.userInfo?.address?.location?.longitude ??
        lead.userInfo?.address?.location?.coordinates?.[0] ??
        lead.surveyData?.stages?.[0]?.form_data?.lng ??
        lead.surveyData?.stages?.[0]?.form_data?.longitude ??
        lead.fieldViewData?.[0]?.lng ??
        lead.fieldViewData?.[0]?.longitude ??
        lead.fieldViewData?.[0]?.form_data?.lng ??
        lead.location?.longitude;

    const lat = Number(rawLat);
    const lng = Number(rawLng);

    if (!isNaN(lat) && !isNaN(lng) && isWithinIndia(lat, lng)) {
        return { lat, lng, isExact: true };
    }
    return { lat: null, lng: null, isExact: false };
};

// Styled Components for Side Details Panel (Matching nector-apiaries Tab 1)
const MapLayoutWrapper = styled.div`
    display: flex;
    gap: 20px;
    align-items: stretch;
    width: 100%;
`;

const MapContainer = styled.div`
    flex: 1;
    min-width: 0;
    transition: all 0.3s ease;
`;

const SideDetailsPanel = styled.div`
    width: 340px;
    background: #ffffff;
    border-radius: 14px;
    padding: 24px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    animation: slideIn 0.25s ease-out;

    @keyframes slideIn {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
    }
`;

const PanelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 12px;
    margin-bottom: 16px;

    span.title {
        font-size: 13px;
        font-weight: 800;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    button.close-btn {
        background: transparent;
        border: none;
        font-size: 18px;
        color: #94a3b8;
        cursor: pointer;
        &:hover { color: #0f172a; }
    }
`;

const BeekeeperName = styled.h4`
    margin: 0 0 6px 0;
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
`;

const DetailFieldGroup = styled.div`
    margin-bottom: 14px;

    label {
        display: block;
        font-size: 11px;
        font-weight: 800;
        color: #94a3b8;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 3px;
    }

    span.val {
        font-size: 14px;
        font-weight: 700;
        color: #334155;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 16px;
`;

const StatBox = styled.div`
    background: #f8fafc;
    border-radius: 10px;
    padding: 12px;
    border: 1px solid #f1f5f9;

    label {
        display: block;
        font-size: 10px;
        font-weight: 800;
        color: #64748b;
        text-transform: uppercase;
        margin-bottom: 2px;
    }

    span.num {
        font-size: 16px;
        font-weight: 800;
        color: ${props => props.color || '#0f172a'};
    }
`;

const ActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;
`;

export default function HouseholdMapComponent({ leads, onSelectLead }) {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: "AIzaSyBg4tz2fOqTqAny-Hph8blHRP9YeRTuIDg"
    });

    const mapInstanceRef = useRef(null);
    const hasFittedRef = useRef(false);
    const [selectedLead, setSelectedLead] = useState(null);

    const validMarkers = useMemo(() => {
        const processed = [];
        const coordMap = new Map();

        leads.forEach((lead) => {
            const coords = extractCoordinates(lead);
            let lat = coords.lat;
            let lng = coords.lng;

            if (!coords.isExact) {
                const distVal = getLeadDistrict(lead);
                const stateVal = getLeadState(lead);

                const distKey = (distVal !== '-' ? distVal : '').toLowerCase().trim();
                const stateKey = (stateVal !== '-' ? stateVal : '').toLowerCase().trim();
                const clusterKey = (lead.clusterName || '').toLowerCase().trim();

                const fallback = LOCATION_CENTROIDS[distKey] || LOCATION_CENTROIDS[clusterKey] || LOCATION_CENTROIDS[stateKey] || { lat: 20.5937, lng: 78.9629 };
                lat = fallback.lat;
                lng = fallback.lng;

                const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
                if (coordMap.has(key)) {
                    const count = coordMap.get(key);
                    const angle = count * (Math.PI / 4);
                    const radius = 0.003 * (1 + Math.floor(count / 6));
                    lat += Math.cos(angle) * radius;
                    lng += Math.sin(angle) * radius;
                    coordMap.set(key, count + 1);
                } else {
                    coordMap.set(key, 1);
                }
            }

            processed.push({ ...lead, lat, lng, isExact: coords.isExact });
        });

        return processed;
    }, [leads]);

    // Single-Execution Bounds Fitting to Prevent Re-render Camera Reset / America Zoom Loops
    const onLoad = useCallback((mapInstance) => {
        mapInstanceRef.current = mapInstance;
        if (!hasFittedRef.current && validMarkers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            validMarkers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
            mapInstance.fitBounds(bounds);
            hasFittedRef.current = true;
        }
    }, [validMarkers]);

    if (loadError) {
        return (
            <div style={{ padding: '40px', background: '#fef2f2', borderRadius: '12px', color: '#991b1b', textAlign: 'center' }}>
                <h5>Google Maps API Key Error</h5>
                <p>Failed to load Google Maps script. Check API Key or Referrer restrictions.</p>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div style={{ height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: '12px' }}>
                <BounceLoader color="#2563eb" size={40} />
            </div>
        );
    }

    const selectedUserId = selectedLead ? (selectedLead.userInfo?._id || selectedLead._id) : null;
    const selectedName = selectedLead ? (selectedLead.userInfo?.name || selectedLead.name || 'N/A') : '';
    const selectedMobile = selectedLead ? (selectedLead.userInfo?.mobileNumber || selectedLead.mobile_number || 'N/A') : '';
    const selectedVillage = selectedLead ? getLeadVillage(selectedLead) : '';
    const selectedBlock = selectedLead ? getLeadBlock(selectedLead) : '';
    const selectedDistrict = selectedLead ? getLeadDistrict(selectedLead) : '';
    const selectedState = selectedLead ? getLeadState(selectedLead) : '';
    const locationStr = [selectedVillage !== '-' && selectedVillage, selectedBlock !== '-' && selectedBlock, selectedDistrict !== '-' && selectedDistrict, selectedState !== '-' && selectedState].filter(Boolean).join(', ');

    return (
        <MapLayoutWrapper>
            <MapContainer>
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={5}
                    center={defaultCenter}
                    onLoad={onLoad}
                    options={{
                        disableDefaultUI: false,
                        zoomControl: true,
                        styles: [
                            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
                        ]
                    }}
                >
                    {validMarkers.map((marker, idx) => (
                        <Marker
                            key={marker._id || idx}
                            position={{ lat: marker.lat, lng: marker.lng }}
                            onClick={() => setSelectedLead(marker)}
                            icon={{
                                url: marker.isExact
                                    ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                                    : 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png'
                            }}
                        />
                    ))}
                </GoogleMap>
            </MapContainer>

            {selectedLead && (
                <SideDetailsPanel>
                    <div>
                        <PanelHeader>
                            <span className="title">Apiary Details</span>
                            <button className="close-btn" onClick={() => setSelectedLead(null)}>✖</button>
                        </PanelHeader>

                        <BeekeeperName>{selectedName}</BeekeeperName>
                        
                        <div style={{ marginBottom: '16px' }}>
                            <Badge variant="success" style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, backgroundColor: '#16a34a' }}>
                                🟢 Household Apiary Active
                            </Badge>
                        </div>

                        <DetailFieldGroup>
                            <label>Location</label>
                            <span className="val">{locationStr || 'N/A'}</span>
                        </DetailFieldGroup>

                        <DetailFieldGroup>
                            <label>Mobile Number</label>
                            <span className="val" style={{ fontFamily: 'monospace' }}>{selectedMobile}</span>
                        </DetailFieldGroup>

                        <StatsGrid>
                            <StatBox color="#16a34a">
                                <label>Hives Count</label>
                                <span className="num">{selectedLead.hivesCount || 10} Hives</span>
                            </StatBox>
                            <StatBox color="#2563eb">
                                <label>Stage</label>
                                <span className="num">{selectedLead.stage === 'advance' ? 'Advanced' : 'Basic'}</span>
                            </StatBox>
                        </StatsGrid>

                        <DetailFieldGroup>
                            <label>Cluster / Project</label>
                            <span className="val" style={{ color: '#7e22ce' }}>{selectedLead.clusterName || 'N/A'}</span>
                        </DetailFieldGroup>

                        <DetailFieldGroup>
                            <label>GPS Coordinate Status</label>
                            <span className="val" style={{ fontSize: '12px', color: selectedLead.isExact ? '#16a34a' : '#d97706' }}>
                                {selectedLead.isExact ? '📍 Verified Exact GPS Location' : '🌐 District Centroid Location'}
                            </span>
                        </DetailFieldGroup>
                    </div>

                    <ActionsContainer>
                        <Button
                            variant="warning"
                            style={{
                                width: '100%',
                                backgroundColor: '#ef5a20',
                                borderColor: '#ef5a20',
                                color: '#fff',
                                fontWeight: 700,
                                borderRadius: '8px',
                                padding: '10px'
                            }}
                            onClick={() => window.open(`/user-profile/${selectedUserId}`, '_blank')}
                        >
                            View Detailed Profile ➔
                        </Button>
                        <Button
                            variant="outline-secondary"
                            style={{
                                width: '100%',
                                fontWeight: 700,
                                borderRadius: '8px',
                                padding: '8px'
                            }}
                            onClick={() => onSelectLead(selectedUserId)}
                        >
                            👁️ Quick View Details
                        </Button>
                    </ActionsContainer>
                </SideDetailsPanel>
            )}
        </MapLayoutWrapper>
    );
}
